/**
 * ConversationList - Sidebar with tabs, search, and thread list
 */

import React, { useState, useMemo } from 'react';
import { Search, Inbox as InboxIcon, Send } from 'lucide-react';
import { motion } from 'framer-motion';
import { useMessages } from './MessagesContext';
import { isImageAvatarUrl } from '../../lib/avatars';

export default function ConversationList() {
  const { threads, activeThreadId, setActiveThreadId, userId, searchThreads, loading } = useMessages();
  const [activeTab, setActiveTab] = useState('received'); // 'received' | 'sent'
  const [searchTerm, setSearchTerm] = useState('');

  console.log('ConversationList: threads:', threads?.length, 'userId:', userId, 'loading:', loading);
  console.log('ConversationList: threads array:', threads);

  // Filter threads by tab - show threads with ANY messages in that direction
  const filteredByTab = useMemo(() => {
    if (!threads || threads.length === 0) {
      console.log('ConversationList: No threads available');
      return [];
    }
    
    console.log('ConversationList: Filtering threads by tab:', activeTab);
    console.log('ConversationList: Sample thread structure:', threads[0]);
    console.log('ConversationList: All threads:', threads);
    
    // TEMPORARILY: Show all threads regardless of tab to debug
    return threads;
    
    /* Original filtering logic - temporarily disabled
    const filtered = threads.filter((thread) => {
      // Check if thread has any messages in the direction we want
      const hasReceivedMessages = thread.messages?.some(msg => msg.recipient_id === userId);
      const hasSentMessages = thread.messages?.some(msg => msg.sender_id === userId);
      
      console.log('Thread', thread.id, '- received:', hasReceivedMessages, 'sent:', hasSentMessages, 'messages:', thread.messages?.length);
      
      if (activeTab === 'received') {
        return hasReceivedMessages;
      } else {
        return hasSentMessages;
      }
    });
    
    console.log('ConversationList: Filtered to', filtered.length, 'threads');
    return filtered;
    */
  }, [threads, activeTab, userId]);

  // Apply search
  const displayThreads = useMemo(() => {
    if (!searchTerm.trim()) {
      console.log('ConversationList: Display threads (no search):', filteredByTab.length);
      return filteredByTab;
    }
    const searched = searchThreads(searchTerm);
    console.log('ConversationList: Display threads (with search):', searched.length);
    return searched;
  }, [filteredByTab, searchTerm, searchThreads]);

  const handleThreadClick = (threadId) => {
    setActiveThreadId(threadId);
  };

  const getOtherUser = (thread) => {
    const otherUserId = thread.participants.find((id) => id !== userId);
    return {
      id: otherUserId,
      name: thread.participantNames[otherUserId] || 'User',
      avatar: thread.participantAvatars[otherUserId] || '👤',
    };
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString();
  };

  return (
    <div className="col-span-4 md:col-span-4 lg:col-span-3 xl:col-span-3 flex flex-col bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/10 bg-black/10">
        <h2 className="text-lg font-bold text-white mb-3">Messages</h2>
        
        {/* Tabs */}
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => setActiveTab('received')}
            className={`flex-1 px-3 py-2 rounded-lg font-semibold text-sm transition-colors ${
              activeTab === 'received'
                ? 'bg-green-600 text-white'
                : 'bg-white/5 text-white/70 hover:bg-white/10'
            }`}
          >
            <InboxIcon className="w-4 h-4 inline-block mr-1" />
            Received
          </button>
          <button
            onClick={() => setActiveTab('sent')}
            className={`flex-1 px-3 py-2 rounded-lg font-semibold text-sm transition-colors ${
              activeTab === 'sent'
                ? 'bg-green-600 text-white'
                : 'bg-white/5 text-white/70 hover:bg-white/10'
            }`}
          >
            <Send className="w-4 h-4 inline-block mr-1" />
            Sent
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/50"
          />
        </div>
      </div>

      {/* Thread List */}
      <div
        role="listbox"
        className="flex-1 overflow-y-auto"
      >
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <div className="text-white/50 text-sm">Loading...</div>
            <div className="text-white/30 text-xs mt-2">userId: {userId || 'null'}</div>
          </div>
        ) : displayThreads.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center mb-3">
              <span className="text-2xl">📭</span>
            </div>
            <p className="text-sm text-white/60">
              {searchTerm ? 'No messages match your search' : 'No messages yet'}
            </p>
            <div className="text-white/30 text-xs mt-2">
              Debug: threads={threads?.length}, filtered={displayThreads.length}, loading={String(loading)}, userId={userId || 'null'}
            </div>
          </div>
        ) : (
          displayThreads.map((thread) => {
            const otherUser = getOtherUser(thread);
            const isActive = thread.id === activeThreadId;

            return (
              <motion.div
                key={thread.id}
                role="option"
                aria-selected={isActive}
                onClick={() => handleThreadClick(thread.id)}
                whileHover={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
                className={`p-4 border-b border-white/5 cursor-pointer transition-colors ${
                  isActive ? 'bg-green-600/20 border-l-4 border-l-green-500' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-xl flex-shrink-0 overflow-hidden">
                    {isImageAvatarUrl(otherUser.avatar) ? (
                      <img
                        src={otherUser.avatar}
                        alt={otherUser.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span>{otherUser.avatar}</span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between mb-1">
                      <h4 className="font-semibold text-white text-sm truncate">
                        {otherUser.name}
                      </h4>
                      <span className="text-xs text-white/40 ml-2 flex-shrink-0">
                        {formatTime(thread.lastMessageTime || thread.updatedAt)}
                      </span>
                    </div>
                    <p className="text-xs text-white/60 truncate mb-1">
                      {thread.lastMessage}
                    </p>
                  </div>

                  {/* Unread indicator */}
                  {thread.unread && (
                    <div
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5"
                      style={{ backgroundColor: 'var(--mudo-accent, #b45309)' }}
                    />
                  )}
                </div>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
}
